import type { ReactNode } from 'react';
import { IconSizes, AlertCircleIcon, CheckIcon } from '@newcore/icons';
import { cn } from '@newcore/ui/cn';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Text } from '@newcore/ui/typography';
import { StatusVariant } from '@/enums/status-variant';

interface StatusContainerProps {
  variant: StatusVariant;
  title: string;
  message?: string;
  icon?: ReactNode;
  children?: ReactNode;
}

const DEFAULT_ICONS: Record<StatusVariant, ReactNode> = {
  [StatusVariant.SUCCESS]: <CheckIcon size={IconSizes.LG} strokeWidth={2.5} />,
  [StatusVariant.ERROR]: (
    <AlertCircleIcon size={IconSizes.LG} strokeWidth={2.25} />
  ),
};

export const StatusContainer = ({
  variant,
  title,
  message,
  icon,
  children,
}: StatusContainerProps) => {
  const isSuccess = variant === StatusVariant.SUCCESS;

  return (
    <div className='flex min-h-96 flex-col items-center justify-center px-8 py-10 text-center'>
      <div
        className={cn(
          'flex h-14 w-14 items-center justify-center rounded-full',
          isSuccess
            ? 'bg-success-primary/10 text-success-primary'
            : 'bg-error-primary/10 text-error-primary'
        )}
      >
        {icon ?? DEFAULT_ICONS[variant]}
      </div>

      <Text
        as='h2'
        size={TextSizes.LG}
        weight={FontWeights.SEMIBOLD}
        className='mt-6 text-primary'
      >
        {title}
      </Text>

      {message && (
        <Text
          as='p'
          size={TextSizes.SM}
          className='mt-1.5 max-w-xs text-secondary'
        >
          {message}
        </Text>
      )}

      {children && (
        <div className='mt-6 flex w-full max-w-xs flex-col items-center gap-2'>
          {children}
        </div>
      )}
    </div>
  );
};
